import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft } from "lucide-react";
import { RelatedPosts, allBlogPosts } from "@/components/blog/RelatedPosts";
import { AuthorCard, authorInfo } from "@/components/blog/AuthorCard";
import { ShareButtons } from "@/components/blog/ShareButtons";

export default function DigitalFrontDoorPost() {
  return (
    <>
      <Helmet>
        <title>The Digital Front Door: Reimagining Healthcare Access | Ranvir Gill</title>
        <meta
          name="description"
          content="Ranvir Gill explores how AI-powered digital front door strategies are transforming healthcare access, creating seamless patient experiences from first symptom to treatment."
        />
        <meta
          name="keywords"
          content="digital front door healthcare, Ranvir Gill, healthcare access, patient engagement platform, digital patient experience, AI algorithm healthcare, CheckFirst, healthcare accessibility"
        />
        <meta name="author" content="Ranvir Gill" />
        
        <meta property="og:title" content="The Digital Front Door: Reimagining Healthcare Access" />
        <meta property="og:description" content="How digital-first patient engagement is creating seamless healthcare experiences." />
        <meta property="og:url" content="https://www.checkfirstcare.com/blog/digital-front-door-healthcare" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://www.checkfirstcare.com/og-blog-digital-front-door.jpg" />
        <meta property="og:site_name" content="CheckFirst" />
        <meta property="article:author" content="Ranvir Gill" />
        <meta property="article:published_time" content="2025-02-05" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="The Digital Front Door: Reimagining Healthcare Access" />
        <meta name="twitter:description" content="How digital-first patient engagement is creating seamless healthcare experiences." />
        <meta name="twitter:image" content="https://www.checkfirstcare.com/og-blog-digital-front-door.jpg" />
        
        <link rel="canonical" href="https://www.checkfirstcare.com/blog/digital-front-door-healthcare" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            "headline": "The Digital Front Door: Reimagining Healthcare Access",
            "description": "How digital-first patient engagement is creating seamless healthcare experiences.",
            "image": [
              "https://www.checkfirstcare.com/og-blog-digital-front-door.jpg"
            ],
            "author": {
              "@type": "Person",
              "name": "Ranvir Gill",
              "jobTitle": "Founder & CEO",
              "url": "https://www.checkfirstcare.com/founder",
              "image": "https://www.checkfirstcare.com/ranvir-gill.jpg",
              "sameAs": [
                "https://www.linkedin.com/in/ranvir-gill-6894b7199",
                "https://twitter.com/ranvirgill"
              ],
              "worksFor": {
                "@type": "Organization",
                "name": "CheckFirst",
                "url": "https://www.checkfirstcare.com"
              }
            },
            "publisher": {
              "@type": "Organization",
              "name": "CheckFirst",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.checkfirstcare.com/og-image.jpg",
                "width": 1200,
                "height": 630
              },
              "url": "https://www.checkfirstcare.com"
            },
            "datePublished": "2025-02-05T08:00:00+00:00",
            "dateModified": "2025-02-05T08:00:00+00:00",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://www.checkfirstcare.com/blog/digital-front-door-healthcare"
            },
            "articleSection": "Digital Health",
            "wordCount": 1100,
            "keywords": ["digital front door", "healthcare access", "patient engagement", "digital health"],
            "isAccessibleForFree": true,
            "inLanguage": "en-US"
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.checkfirstcare.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://www.checkfirstcare.com/blog"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "Digital Front Door",
                "item": "https://www.checkfirstcare.com/blog/digital-front-door-healthcare"
              }
            ]
          })}
        </script>
      </Helmet>

      <article className="min-h-screen">
        {/* Header */}
        <section className="py-12 lg:py-20 bg-gradient-to-br from-primary/10 to-secondary/30">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <Button variant="ghost" asChild className="mb-6">
                <Link to="/blog">
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back to Blog
                </Link>
              </Button>
              <span className="text-sm font-medium text-primary bg-primary/10 px-3 py-1 rounded-full">
                Digital Health
              </span>
              <h1 className="text-3xl lg:text-5xl font-bold text-foreground mt-4 mb-6">
                The Digital Front Door: Reimagining Healthcare Access
              </h1>
              <AuthorCard 
                {...authorInfo}
                date="February 5, 2025"
                readTime="6 min read"
              />
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-12 lg:py-16">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-3xl mx-auto prose prose-lg dark:prose-invert">
              <p className="lead text-xl text-muted-foreground">
                The concept of a "digital front door" in healthcare represents a fundamental shift in how patients access and interact with health systems. As the founder of CheckFirstCare, I believe this approach is essential for creating truly patient-centered care.
              </p>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">What Is the Digital Front Door?</h2>
              <p className="text-muted-foreground">
                The digital front door is a unified, digital entry point that connects patients to all available healthcare services. Just as a hospital's physical front door welcomes patients into a facility, the digital front door welcomes them into a comprehensive digital healthcare ecosystem.
              </p>
              <p className="text-muted-foreground">
                This includes:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Symptom checking and triage</li>
                <li>Appointment scheduling</li>
                <li>Telehealth visits</li>
                <li>Patient portal access</li>
                <li>Care navigation and wayfinding</li>
                <li>Health education and resources</li>
              </ul>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Why It Matters Now</h2>
              <p className="text-muted-foreground">
                The COVID-19 pandemic accelerated digital health adoption by years. Patients now expect the same digital convenience in healthcare that they experience in banking, retail, and entertainment. Healthcare organizations that fail to meet these expectations risk losing patients to more digitally-savvy competitors.
              </p>

              <div className="grid md:grid-cols-2 gap-6 my-8">
                <div className="bg-secondary/50 rounded-lg p-6 text-center">
                  <div className="text-3xl font-bold text-primary">76%</div>
                  <div className="text-sm text-muted-foreground">of patients want digital self-service options</div>
                </div>
                <div className="bg-secondary/50 rounded-lg p-6 text-center">
                  <div className="text-3xl font-bold text-primary">60%</div>
                  <div className="text-sm text-muted-foreground">would switch providers for better digital experience</div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Key Components of an Effective Digital Front Door</h2>
              
              <h3 className="text-xl font-bold text-foreground mt-6 mb-3">1. Intelligent Symptom Assessment</h3>
              <p className="text-muted-foreground">
                At the heart of any digital front door strategy is the ability to help patients understand their symptoms and navigate to appropriate care. AI-powered symptom checkers can guide patients to the right level of care—whether that's self-care at home, a virtual visit, urgent care, or the emergency room.
              </p>

              <h3 className="text-xl font-bold text-foreground mt-6 mb-3">2. Seamless Navigation</h3>
              <p className="text-muted-foreground">
                Once a patient knows what care they need, finding and accessing that care should be frictionless. This means integrated scheduling, transparent pricing, and clear directions—both physical and virtual.
              </p>

              <h3 className="text-xl font-bold text-foreground mt-6 mb-3">3. Personalized Experience</h3>
              <p className="text-muted-foreground">
                The digital front door should recognize returning patients and personalize their experience based on their health history, preferences, and past interactions with the health system.
              </p>

              <h3 className="text-xl font-bold text-foreground mt-6 mb-3">4. 24/7 Availability</h3>
              <p className="text-muted-foreground">
                Health concerns don't follow business hours. A true digital front door is always open, providing guidance and access to care whenever patients need it.
              </p>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">The CheckFirst Approach</h2>
              <p className="text-muted-foreground">
                At CheckFirst, we've built our platform around the digital front door philosophy. We believe that the first interaction a patient has with the healthcare system sets the tone for their entire care journey.
              </p>
              <p className="text-muted-foreground">
                By providing intelligent, compassionate guidance at that critical first moment, we help patients get the right care faster while reducing unnecessary burden on emergency departments.
              </p>

              <div className="bg-primary/10 rounded-lg p-6 mt-8">
                <p className="text-foreground font-medium mb-2">About the Author</p>
                <p className="text-muted-foreground text-sm">
                  <strong><Link to="/about" className="hover:text-primary">Ranvir Gill</Link></strong> is the founder and CEO of CheckFirst. He is committed to making healthcare more accessible and patient-centered through innovative digital solutions.
                </p>
              </div>

              <div className="mt-8 pt-8 border-t border-border">
                <ShareButtons
                  url="https://www.checkfirstcare.com/blog/digital-front-door-healthcare"
                  title="The Digital Front Door: Reimagining Healthcare Access"
                  description="How digital-first patient engagement is creating seamless healthcare experiences from the first symptom to treatment completion."
                />
              </div>
            </div>
          </div>
        </section>

        {/* Related Posts */}
        <RelatedPosts currentSlug="digital-front-door-healthcare" posts={allBlogPosts} />

        {/* CTA */}
        <section className="py-12 bg-secondary/50">
          <div className="container mx-auto px-4 lg:px-8 text-center">
            <div className="max-w-2xl mx-auto space-y-4">
              <h3 className="text-2xl font-bold text-foreground">
                Build Your Digital Front Door
              </h3>
              <p className="text-muted-foreground">
                Learn how CheckFirstCare can be the intelligent first touchpoint for your patients.
              </p>
              <Button asChild size="lg">
                <Link to="/contact">Schedule a Consultation</Link>
              </Button>
            </div>
          </div>
        </section>
      </article>
    </>
  );
}
